/**
 * AITestModal - AI 테스트 생성 모달
 *
 * 추출된 문제 목록에서 선택/해제하고 테스트를 생성하는 모달
 * 문제 미리보기 영역에서 실제 문제 콘텐츠를 인라인으로 표시
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../utils/latexParser';
import { useAuth } from '../../context/AuthContext';
import { Modal, Button } from '../common';
import { MathceptDetailModal, useMathceptDetail } from '../common';
import { Check, Loader2, BookOpen } from 'lucide-react';
import { fetchMathConcepts as fetchMathConceptsUtil } from '../../utils/mathConceptsUtils';
import { DIFFICULTY_COLORS } from '../../pages/LearningStatus/utils/constants';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 날짜 포맷 함수
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

// 난이도별 색상 (Tailwind 클래스 - 분포 차트용)
const getDifficultyColorClass = (difficulty) => {
  const colors = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-blue-100 text-blue-800',
    3: 'bg-yellow-100 text-yellow-800',
    4: 'bg-orange-100 text-orange-800',
    5: 'bg-red-100 text-red-800',
  };
  return colors[difficulty] || 'bg-gray-100 text-gray-800';
};

// 난이도별 색상 (rgba - ProblemPreview 스타일)
const getDifficultyColor = (difficulty) => {
  return DIFFICULTY_COLORS[difficulty - 1] || 'rgba(156, 163, 175, 0.7)';
};

// 개념 타입별 색상
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',
    'AXM': 'rgba(249, 115, 22, 0.5)',
    'IDN': 'rgba(245, 158, 11, 0.5)',
    'THM': 'rgba(34, 197, 94, 0.5)',
    'MTH': 'rgba(6, 182, 212, 0.5)',
    'PRP': 'rgba(59, 130, 246, 0.5)',
    'FOR': 'rgba(139, 92, 246, 0.5)',
    'NTN': 'rgba(236, 72, 153, 0.5)'
  };
  return colors[type] || 'rgba(251, 146, 60, 0.5)';
};

function AITestModal({
  isOpen,
  onClose,
  studentId,
  studentName,
  stamp,
  problems,
  onTestCreated
}) {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  // 선택된 문제 ID Set
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(problems.map(p => p.problem_id))
  );
  // 시험지 이름
  const [presetName, setPresetName] = useState(() => {
    const now = new Date();
    const hhmm = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
    return `AI_${stamp?.tree_name || ''}_${studentName || '학생'}_${formatDate(now)}_${hhmm}`;
  });
  // 생성 중 상태
  const [isCreating, setIsCreating] = useState(false);

  // 문제 콘텐츠 관련 상태
  const [previewData, setPreviewData] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [mathConcepts, setMathConcepts] = useState([]);

  // 매스컨셉 상세 모달
  const { isModalOpen: mathceptDetailModalOpen, loading: mathceptDetailLoading, data: mathceptDetailData, fetchAndShow: fetchMathceptDetails, closeModal: closeMathceptDetailModal } = useMathceptDetail({ handleAuth: false });

  // API 호출 유틸
  const fetchWithTokenRefresh = useCallback(async (url) => {
    const token = await getAccessToken();
    if (!token) throw new Error('인증 토큰이 없습니다.');
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error('API 요청 실패');
    return await response.json();
  }, [getAccessToken]);

  // Lexical editor 인스턴스 캐싱 (1회만 생성)
  const editorRef = useRef(null);
  const getEditor = useCallback(() => {
    if (!editorRef.current) {
      editorRef.current = createEditor({ nodes: [...PlaygroundNodes] });
    }
    return editorRef.current;
  }, []);

  // Lexical → HTML 변환
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = getEditor();
    const processNode = (node) => {
      node = { ...node };
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };
    const data = { ...lexicalData, root: processNode(lexicalData.root) };
    const editorState = editor.parseEditorState(data);
    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => { content = $generateHtmlFromNodes(editor); });
    return content;
  }, [getEditor]);

  // LaTeX 렌더링
  const renderLatex = (text) => {
    if (!text) return '';
    try {
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, { throwOnError: false, displayMode: false });
        } catch (e) { return match; }
      });
    } catch (error) { return text; }
  };

  // 문제 콘텐츠 로드
  useEffect(() => {
    if (!isOpen || problems.length === 0) return;

    let isMounted = true;
    const loadPreviewData = async () => {
      setIsLoadingPreview(true);
      try {
        // Math concepts 로드
        let currentMathConcepts = mathConcepts;
        if (currentMathConcepts.length === 0) {
          try {
            const concepts = await fetchMathConceptsUtil();
            if (isMounted) setMathConcepts(concepts);
            currentMathConcepts = concepts;
          } catch (error) {
            console.error('Math concepts 로드 실패:', error);
          }
        }

        const problemIds = problems.map(p => p.problem_id);

        // 50개씩 배치 분할
        const BATCH_SIZE = 50;
        const batches = [];
        for (let i = 0; i < problemIds.length; i += BATCH_SIZE) {
          batches.push(problemIds.slice(i, i + BATCH_SIZE));
        }

        // 모든 배치를 병렬 호출 (print + mathcepts)
        const batchResults = await Promise.all(
          batches.map(async (batch) => {
            const ids = batch.join(',');
            const [printRes, mathceptsRes] = await Promise.all([
              fetchWithTokenRefresh(`/api/problems/print/page?problem_ids=${ids}&page=1&per_page=${BATCH_SIZE}`),
              fetchWithTokenRefresh(`/api/mathcepts/temp/by-prob-ids?problem_ids=${ids}`).catch(() => null),
            ]);
            return { printRes, mathceptsRes };
          })
        );

        if (!isMounted) return;

        // 문제 콘텐츠 맵
        const contentMap = {};
        batchResults.forEach(({ printRes }) => {
          if (printRes?.status === 'success' && printRes.data) {
            printRes.data.forEach(item => {
              try {
                contentMap[item.problem_id] = convertLexicalToHtml(item.lexical_data.lexical_data);
              } catch (e) {
                contentMap[item.problem_id] = null;
              }
            });
          }
        });

        // mathcepts 맵
        const conceptDataMap = {};
        batchResults.forEach(({ mathceptsRes }) => {
          if (mathceptsRes?.status === 'success' && mathceptsRes?.data) {
            mathceptsRes.data.forEach(item => {
              conceptDataMap[item.problem_id] = {
                concept_ids: item.concept_ids || [],
                difficulty: item.difficulty
              };
            });
          }
        });

        // 각 문제별 미리보기 데이터 구성
        const previews = problems.map(p => {
          const conceptData = conceptDataMap[p.problem_id] || {};
          const conceptIds = conceptData.concept_ids || [];

          // concept_ids → 매칭된 개념 정보
          const matchedConcepts = {};
          conceptIds.forEach(cid => {
            const mc = currentMathConcepts.find(c => c.id === cid || String(c.id) === String(cid));
            if (mc) {
              matchedConcepts[cid] = {
                concept_name: mc.concept_name || mc.text,
                order: mc.order,
                concept_type: mc.concept_type,
              };
            }
          });

          return {
            problem_id: p.problem_id,
            difficulty: conceptData.difficulty || p.difficulty,
            address: p.address,
            content: contentMap[p.problem_id] || null,
            matchedConcepts,
          };
        });

        if (isMounted) setPreviewData(previews);
      } catch (error) {
        console.error('문제 미리보기 로드 실패:', error);
      } finally {
        if (isMounted) setIsLoadingPreview(false);
      }
    };

    loadPreviewData();
    return () => { isMounted = false; };
  }, [isOpen, problems, fetchWithTokenRefresh, convertLexicalToHtml]);

  // 선택된 문제 목록
  const selectedProblems = useMemo(
    () => problems.filter(p => selectedIds.has(p.problem_id)),
    [problems, selectedIds]
  );

  // 난이도별 분포
  const difficultyDistribution = useMemo(() => {
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    selectedProblems.forEach(p => {
      if (p.difficulty && dist.hasOwnProperty(p.difficulty)) {
        dist[p.difficulty]++;
      }
    });
    return dist;
  }, [selectedProblems]);

  // 문제 선택/해제
  const handleToggle = (problemId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(problemId)) {
        next.delete(problemId);
      } else {
        next.add(problemId);
      }
      return next;
    });
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedIds.size === problems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(problems.map(p => p.problem_id)));
    }
  };

  // 테스트 생성
  const handleCreate = async () => {
    if (selectedProblems.length === 0) {
      alert('최소 1개 이상의 문제를 선택해주세요.');
      return;
    }

    if (!presetName.trim()) {
      alert('시험지 이름을 입력해주세요.');
      return;
    }

    setIsCreating(true);
    try {
      const token = await getAccessToken();

      // 1. 기존 프리셋 목록에서 동일 이름 확인
      const listResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`,
        {
          headers: { 'Authorization': token, 'Content-Type': 'application/json' }
        }
      );

      if (!listResponse.ok) {
        throw new Error('프리셋 목록 조회 실패');
      }

      const listResult = await listResponse.json();
      const presets = listResult.data || [];
      const existingNames = new Set(presets.map(p => p.preset_name));

      // 2. 중복 이름이면 (2), (3) ... 접미사를 붙여 고유 이름 생성
      let finalName = presetName.trim();
      if (existingNames.has(finalName)) {
        let suffix = 2;
        while (existingNames.has(`${presetName.trim()}(${suffix})`)) {
          suffix++;
        }
        finalName = `${presetName.trim()}(${suffix})`;
      }

      const presetPayload = {
        preset_name: finalName,
        description: 'AI 추출 문제',
        preset_info: selectedProblems.map((p, idx) => ({
          problem_id: p.problem_id,
          address: p.address,
          step: p.step,
          sequence: idx + 1
        }))
      };

      // 3. 새 프리셋 생성
      const presetResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(presetPayload)
        }
      );

      if (!presetResponse.ok) {
        const errorData = await presetResponse.json().catch(() => ({}));
        console.error('Preset 생성 실패:', presetResponse.status, errorData);
        throw new Error(errorData.message || `Preset 생성 실패 (${presetResponse.status})`);
      }

      const createResult = await presetResponse.json();
      let presetId = createResult?.data?.preset_id;

      // 응답에서 ID를 못 받으면 목록 재조회
      if (!presetId) {
        const refreshResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`,
          {
            headers: { 'Authorization': token, 'Content-Type': 'application/json' }
          }
        );
        if (refreshResponse.ok) {
          const refreshResult = await refreshResponse.json();
          const refreshed = (refreshResult.data || []).find(p => p.preset_name === finalName);
          presetId = refreshed?.preset_id;
        }
      }

      if (!presetId) {
        throw new Error('프리셋 ID를 찾지 못했습니다.');
      }

      // 3. 테스트 생성
      const examResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp.stamp_id}/preset`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            preset_id: presetId,
            solved_exception: false
          })
        }
      );

      if (!examResponse.ok) {
        const errorData = await examResponse.json().catch(() => ({}));
        console.error('테스트 생성 실패:', examResponse.status, errorData);
        if (examResponse.status === 409) {
          alert('테스트 생성 불가: 아직 결과가 입력되지 않은 테스트가 있습니다.\n기존 테스트의 결과를 먼저 입력하거나 삭제한 후 다시 시도해주세요.');
          return;
        }
        if (examResponse.status === 422) {
          alert(`테스트 생성 실패: 요청 데이터 형식 오류\n${errorData.message || JSON.stringify(errorData)}`);
          return;
        }
        throw new Error(errorData.message || '테스트 생성 실패');
      }

      alert('테스트가 성공적으로 생성되었습니다.');
      onTestCreated?.();
      onClose();
      navigate(`/student/${studentId}/stamp/${stamp.stamp_id}/result`);

    } catch (error) {
      console.error('테스트 생성 실패:', error);
      alert(`테스트 생성에 실패했습니다: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`AI 테스트 생성 - ${stamp?.tree_name || ''}`}
      size="lg"
      maxHeight="90vh"
      fixedHeight
      bodyClassName="overflow-hidden flex flex-col"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={selectedProblems.length === 0 || isCreating}
            className="flex items-center gap-2"
          >
            {isCreating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {isCreating ? '생성 중...' : '테스트 생성'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        {/* 시험지 이름 입력 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            시험지 이름
          </label>
          <input
            type="text"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="시험지 이름을 입력하세요"
          />
          <p className="text-xs text-gray-500 mt-1">
            (자동 생성, 수정 가능)
          </p>
        </div>

        {/* 난이도 분포 */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">난이도 분포</h4>
            <span className="text-sm font-medium text-gray-700">
              난이도 평균 {selectedProblems.length > 0 ? (selectedProblems.reduce((sum, p) => sum + (p.difficulty || 0), 0) / selectedProblems.length).toFixed(2) : '-'}
            </span>
          </div>
            <div className="space-y-1">
              {[1, 2, 3, 4, 5].map((level) => {
                const count = difficultyDistribution[level];
                const maxCount = Math.max(...Object.values(difficultyDistribution), 1);
                const percent = (count / maxCount) * 100;
                return (
                  <div key={level} className="flex items-center gap-2">
                    <span className="text-xs w-8">Lv.{level}</span>
                    <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
                      <div
                        className={`h-full ${getDifficultyColorClass(level).split(' ')[0]}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-xs w-12 text-right">{count}문제</span>
                  </div>
                );
              })}
            </div>
        </div>

        {/* 문제 미리보기 */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-center gap-3 mb-2 shrink-0">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap shrink-0">
              문제 미리보기 (<span className="inline-block text-right tabular-nums" style={{ width: `${String(problems.length).length}ch` }}>{selectedIds.size}</span>/{problems.length})
            </span>
            <div className="relative flex-1 h-8">
              <div className="absolute top-2 left-0 right-0 h-1 rounded bg-gray-200"></div>
              <div
                className="absolute top-2 h-1 rounded bg-primary"
                style={{ left: '0%', right: `${problems.length > 0 ? (1 - selectedIds.size / problems.length) * 100 : 100}%` }}
              ></div>
              <input
                type="range"
                min={0}
                max={problems.length}
                value={selectedIds.size}
                onChange={(e) => {
                  const count = parseInt(e.target.value);
                  const ids = problems.slice(0, count).map(p => p.problem_id);
                  setSelectedIds(new Set(ids));
                }}
                className="absolute top-0 left-0 z-20 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
              />
              <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                <span className="w-8 text-[9px] text-gray-400 tabular-nums">0</span>
                <span className="w-8 text-[9px] text-gray-400 tabular-nums text-right">{problems.length}</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedIds.size === problems.length ? '전체 해제' : '전체 선택'}
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 ai-test-preview-scroll" style={{ scrollbarWidth: 'thin' }}>
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
              </div>
            ) : problems.length > 0 ? (
              <div className="space-y-3">
                {problems.map((problem) => {
                  const preview = previewData.find(pd => pd.problem_id === problem.problem_id);
                  const isSelected = selectedIds.has(problem.problem_id);

                  return (
                    <div
                      key={problem.problem_id}
                      className={`rounded-3xl border border-gray-200 bg-white p-3 shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20 cursor-pointer ${isSelected ? 'border-primary/20' : ''}`}
                      onClick={() => handleToggle(problem.problem_id)}
                    >
                      {/* 상단: 체크박스 + PID + 난이도 + address */}
                      <div className="mb-1 flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggle(problem.problem_id)}
                            className="w-4 h-4 text-primary rounded focus:ring-primary cursor-pointer"
                          />
                          <span className="text-xs text-gray-500">PID: {problem.problem_id}</span>
                          {(preview?.difficulty || problem.difficulty) && (
                            <div
                              className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                              style={{ backgroundColor: getDifficultyColor(preview?.difficulty || problem.difficulty) }}
                            >
                              Lv.{preview?.difficulty || problem.difficulty}
                            </div>
                          )}
                        </div>
                        {problem.address && (
                          <span className="text-xs text-gray-500">{problem.address}</span>
                        )}
                      </div>

                      {/* 수학 개념 뱃지 */}
                      {preview?.matchedConcepts && Object.keys(preview.matchedConcepts).length > 0 && (
                        <div className="mb-3 rounded-lg bg-gray-50 p-2">
                          <div className="flex items-center gap-2 mb-1">
                            <BookOpen className="w-4 h-4 text-gray-600" />
                            <span className="text-xs font-medium text-gray-600">수학 개념</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(preview.matchedConcepts).map(([id, matched]) => (
                              <div
                                key={id}
                                className="flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-white transition-colors"
                                onClick={(e) => { e.stopPropagation(); fetchMathceptDetails(id); }}
                                title="클릭하여 상세 정보 보기"
                              >
                                {matched?.order && (
                                  <span
                                    className="inline-flex items-center justify-center font-semibold text-gray-700"
                                    style={{
                                      backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                      borderRadius: '6px',
                                      width: '24px', height: '16px', minWidth: '24px', fontSize: '9px'
                                    }}
                                  >
                                    {matched.order}
                                  </span>
                                )}
                                {matched?.concept_type && (
                                  <span
                                    className="inline-flex items-center justify-center font-semibold text-gray-800"
                                    style={{
                                      backgroundColor: getConceptTypeColor(matched.concept_type),
                                      borderRadius: '6px',
                                      width: '24px', height: '16px', minWidth: '24px', fontSize: '8px'
                                    }}
                                  >
                                    {matched.concept_type}
                                  </span>
                                )}
                                <span dangerouslySetInnerHTML={{ __html: renderLatex(matched.concept_name) }} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 문제 콘텐츠 */}
                      <div className="problem-content-wrapper">
                        {preview?.content ? (
                          <div
                            className="problem-content"
                            dangerouslySetInnerHTML={{ __html: preview.content }}
                          />
                        ) : (
                          !isLoadingPreview && (
                            <div className="text-xs text-gray-400 py-2">콘텐츠를 불러올 수 없습니다.</div>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                추출된 문제가 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 매스컨셉 상세 정보 모달 */}
      <MathceptDetailModal
        isOpen={mathceptDetailModalOpen}
        onClose={closeMathceptDetailModal}
        data={mathceptDetailData}
        loading={mathceptDetailLoading}
        renderLatex={renderLatex}
        showCloseButton={false}
        useLoader={false}
        handleAuth={false}
      />

      <style>{`
        .problem-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 500 !important;
          font-size: 18px !important;
        }
        .katex {
          font-size: 1em !important;
        }
        .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
        .problem-content-wrapper {
          display: flex;
          align-items: flex-start;
          width: 100%;
        }
        .problem-content {
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .ai-test-preview-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .ai-test-preview-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .ai-test-preview-scroll::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 2px;
        }
        .ai-test-preview-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
      `}</style>
    </Modal>
  );
}

export default AITestModal;
